import React, { useState,useEffect } from "react";
import { useNavigate,useParams } from "react-router-dom";
import {
  Calendar,
  DollarSign,
  Clock,
  Plus,
  Save,
  X,
  Globe,
  Loader2,
  Users,
  Building,
  Sparkles,
  ImageIcon,
  ChevronRight,
  Target,
} from "lucide-react";
import ImageUpload from "../components/ImageUpload";

const CreateEventCollege = () => {
  const { id } = useParams(); // Get event ID if editing
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id); // Set initial loading if editing
  const [error, setError] = useState("");
  const [event, setEvent] = useState({
    title: "",
    description: "",
    type: "",
    venue: {
      name: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      isVirtual: false,
      meetingLink: "",
    },
    schedule: {
      startDate: "",
      endDate: "",
      timezone: "IST",
    },
    capacity: {
      max: "",
    },
    registration: {
      deadline: "",
      fee: {
        amount: "",
        currency: "INR",
      },
      isRequired: true,
      targetBatch: {  // Add this initialization
        year: null,
        description: "All Batches"
      }
    },
    agenda: [],
    images: [],
  });

  // Add useEffect to fetch event data when editing
  useEffect(() => {
    if (id) {
      fetchEventDetails();
    }
  }, [id]);
  const eventTypes = [
    { value: "reunion", label: "Alumni Reunion" },
    { value: "workshop", label: "Workshop" },
    { value: "seminar", label: "Seminar" },
    { value: "other", label: "Other" },
  ];

  const [newAgendaItem, setNewAgendaItem] = useState({
    time: "",
    title: "",
    description: "",
    speakers: [""], // Changed to array for multiple speakers
  });

  const formSteps = [
    {
      id: "basics",
      title: "Basic Info",
      icon: Sparkles,
      description: "Start with the essentials",
    },
    {
      id: "venue",
      title: "Venue",
      icon: Building,
      description: "Where will it happen?",
    },
    {
      id: "schedule",
      title: "Schedule",
      icon: Calendar,
      description: "When will it take place?",
    },
    {
      id: "registration",
      title: "Registration",
      icon: Users,
      description: "Manage participants",
    },
    {
      id: "agenda",
      title: "Agenda",
      icon: Target,
      description: "Plan your event",
    },
  ];
  const addSpeaker = () => {
    setNewAgendaItem({
      ...newAgendaItem,
      speakers: [...newAgendaItem.speakers, ""],
    });
  };

  const updateSpeaker = (index, value) => {
    const updatedSpeakers = [...newAgendaItem.speakers];
    updatedSpeakers[index] = value;
    setNewAgendaItem({
      ...newAgendaItem,
      speakers: updatedSpeakers,
    });
  };

  const removeSpeaker = (index) => {
    const updatedSpeakers = newAgendaItem.speakers.filter(
      (_, i) => i !== index
    );
    setNewAgendaItem({
      ...newAgendaItem,
      speakers: updatedSpeakers,
    });
  };
 
  const fetchEventDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/events/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch event details");
      }

      const data = await response.json();
      
      // Transform API data to match form structure
      setEvent({
        title: data.title || "",
        description: data.description || "",
        type: data.type || "",
        venue: {
          name: data.venue?.name || "",
          address: data.venue?.address || "",
          city: data.venue?.city || "",
          state: data.venue?.state || "",
          pincode: data.venue?.pincode || "",
          isVirtual: data.venue?.isVirtual || false,
          meetingLink: data.venue?.meetingLink || "",
        },
        schedule: {
          startDate: data.schedule?.startDate 
            ? new Date(data.schedule.startDate).toISOString().slice(0, 16)
            : "",
          endDate: data.schedule?.endDate
            ? new Date(data.schedule.endDate).toISOString().slice(0, 16)
            : "",
          timezone: data.schedule?.timezone || "IST",
        },
        capacity: {
          max: data.capacity?.max || "",
        },
        registration: {
          deadline: data.registration?.deadline
            ? new Date(data.registration.deadline).toISOString().slice(0, 16)
            : "",
          fee: {
            amount: data.registration?.fee?.amount || "",
            currency: data.registration?.fee?.currency || "INR",
          },
          isRequired: data.registration?.isRequired ?? true,
        },
        agenda: data.agenda || [],
        images: data.images || [],
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
  
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }
  
      // Basic validation
      if (!event.title || !event.type || !event.description) {
        throw new Error("Please fill in all required fields");
      }
  
      if (!event.schedule.startDate || !event.schedule.endDate) {
        throw new Error("Please set event start and end dates");
      }
  
      const submitData = {
        ...event,
        college: localStorage.getItem("collegeId"),
        organizer: {
          type: "College",
          organizedBy: localStorage.getItem("collegeId"),
        },
        capacity: {
          max: parseInt(event.capacity.max) || 0,
          remaining: parseInt(event.capacity.max) || 0,
        },
        schedule: {
          startDate: new Date(event.schedule.startDate).toISOString(),
          endDate: new Date(event.schedule.endDate).toISOString(),
          timezone: "IST",
        },
        registration: {
          ...event.registration,
          deadline: event.registration.deadline
            ? new Date(event.registration.deadline).toISOString()
            : null,
          fee: {
            amount: parseInt(event.registration.fee.amount) || 0,
            currency: event.registration.fee.currency,
          },
        },
        agenda: event.agenda.map((item) => ({
          time: new Date(item.time).toISOString(),
          title: item.title,
          description: item.description || "",
          speakers: item.speakers.filter((speaker) => speaker.trim() !== ""),
        })),
        images: event.images.filter((img) => img),
      };
  
      // Determine if creating or updating
      const url = id
        ? `${process.env.REACT_APP_BASE_URL}/api/events/${id}`
        : `${process.env.REACT_APP_BASE_URL}/api/events`;
  
      const response = await fetch(url, {
        method: id ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${id ? 'update' : 'create'} event`);
      }
  
      // Show success message
      alert(`Event ${id ? 'updated' : 'created'} successfully!`);
      navigate("/college/events");
    } catch (err) {
      console.error(`Error ${id ? 'updating' : 'creating'} event:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while fetching event data
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-gray-600 animate-pulse">Loading event details...</p>
        </div>
      </div>
    );
  }
  const addAgendaItem = () => {
    if (newAgendaItem.time && newAgendaItem.title) {
      const filteredSpeakers = newAgendaItem.speakers.filter(
        (speaker) => speaker.trim() !== ""
      );
      setEvent({
        ...event,
        agenda: [
          ...event.agenda,
          { ...newAgendaItem, speakers: filteredSpeakers },
        ],
      });
      setNewAgendaItem({
        time: "",
        title: "",
        description: "",
        speakers: [""],
      });
    }
  };

  const removeAgendaItem = (index) => {
    const newAgenda = [...event.agenda];
    newAgenda.splice(index, 1);
    setEvent({ ...event, agenda: newAgenda });
  };

  const ProgressStep = ({ step, index, currentStep, onClick }) => {
    const Icon = step.icon;
    return (
      <div 
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick();
          }
        }}
        className="flex flex-col items-center cursor-pointer"
      >
        <div
          className={`flex items-center justify-center w-12 h-12 rounded-full mb-2
          transition-all duration-200 ${
            index <= currentStep
              ? "bg-blue-50 border-2 border-blue-500"
              : "bg-gray-100 border-2 border-transparent"
          }`}
        >
          <Icon
            className={`h-5 w-5 ${
              index <= currentStep ? "text-blue-600" : "text-gray-400"
            }`}
          />
        </div>
        <div className="text-center">
          <p
            className={`text-sm font-medium ${
              index <= currentStep ? "text-gray-900" : "text-gray-500"
            }`}
          >
            {step.title}
          </p>
          <p className="text-xs text-gray-500 hidden md:block">
            {step.description}
          </p>
        </div>
      </div>
    );
  };
 

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col items-center text-center">
            <div className="p-3 bg-blue-50 rounded-xl mb-6">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {id ? 'Edit Event' : 'Create a New Event'}
            </h1>
            <p className="text-lg text-white/80 max-w-2xl">
              {id ? 'Update your event details' : 'Organize impactful events and bring your community together'}
            </p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="relative -mt-8 mb-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex justify-between">
            {formSteps.map((step, index) => (
  <ProgressStep
    key={step.id}
    step={step}
    index={index}
    currentStep={currentStep}
    onClick={() => setCurrentStep(index)}
  />
))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Section */}
          <div className={`${currentStep === 0 ? "block" : "hidden"}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Images Upload */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Event Images
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Add up to 3 event images
                        </p>
                      </div>
                      <ImageIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {[...Array(3)].map((_, index) => (
                        <div
                          key={index}
                          className="relative aspect-video bg-gray-50 rounded-lg overflow-hidden"
                        >
                          <ImageUpload
                            onImageUpload={(url) => {
                              const newImages = [...event.images];
                              newImages[index] = url;
                              setEvent({ ...event, images: newImages });
                            }}
                            existingImage={event.images[index]}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 rounded text-xs text-white">
                            {index === 0 ? "Cover Image" : `Image ${index + 1}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Details */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Event Details
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Basic information about your event
                        </p>
                      </div>
                      <Sparkles className="h-5 w-5 text-gray-400" />
                    </div>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Event Title
                        </label>
                        <input
                          type="text"
                          name="title"
                          required
                          value={event.title}
                          onChange={(e) =>
                            setEvent({ ...event, title: e.target.value })
                          }
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                          placeholder="Give your event a clear title"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Event Type
                        </label>
                        <select
  required
  value={event.type}
  onChange={(e) => setEvent({ ...event, type: e.target.value })}
  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
>
  <option value="">Select Type</option>
  <option value="reunion">Alumni Reunion</option>
  <option value="workshop">Professional Workshop</option>
  <option value="seminar">Educational Seminar</option>
  <option value="networking">Networking Event</option>
  <option value="mentorship">Mentorship Program</option>
  <option value="career_fair">Career Fair</option>
  <option value="panel_discussion">Panel Discussion</option>
  <option value="training">Professional Training</option>
  <option value="conference">Industry Conference</option>
  <option value="hackathon">Hackathon</option>
  <option value="webinar">Webinar</option>
  <option value="social_gathering">Social Gathering</option>
  <option value="award_ceremony">Award Ceremony</option>
  <option value="orientation">New Alumni Orientation</option>
  <option value="other">Other</option>
</select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Event Description
                        </label>
                        <textarea
                        name="description"
                     
                          required
                          rows={4}
                          value={event.description}
                          onChange={(e) =>
                            setEvent({ ...event, description: e.target.value })
                          }
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                          placeholder="Provide a detailed description of your event"
                        />
                      </div>

                        {/* Add this new section */}
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Target Batch
    </label>
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <input
          type="radio"
          id="allBatches"
          checked={!event.registration.targetBatch?.year}
          onChange={() => 
            setEvent({
              ...event,
              registration: {
                ...event.registration,
                targetBatch: {
                  year: null,
                  description: "All Batches"
                }
              }
            })
          }
          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
        />
        <label htmlFor="allBatches" className="text-sm text-gray-700">
          All Batches
        </label>
      </div>
      
      <div className="flex items-center space-x-2">
        <input
          type="radio"
          id="specificBatch"
          checked={!!event.registration.targetBatch?.year}
          onChange={() => {
            if (!event.registration.targetBatch?.year) {
              setEvent({
                ...event,
                registration: {
                  ...event.registration,
                  targetBatch: {
                    year: new Date().getFullYear(),
                    description: `Batch ${new Date().getFullYear()}`
                  }
                }
              });
            }
          }}
          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
        />
        <label htmlFor="specificBatch" className="text-sm text-gray-700">
          Specific Batch
        </label>
      </div>

      {event.registration.targetBatch?.year && (
        <div className="ml-6 mt-2 space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Batch Year
            </label>
            <input
              type="number"
              min="1900"
              max={new Date().getFullYear()}
              value={event.registration.targetBatch.year}
              onChange={(e) =>
                setEvent({
                  ...event,
                  registration: {
                    ...event.registration,
                    targetBatch: {
                      year: parseInt(e.target.value),
                      description: `Batch ${e.target.value}`
                    }
                  }
                })
              }
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <input
              type="text"
              value={event.registration.targetBatch.description}
              onChange={(e) =>
                setEvent({
                  ...event,
                  registration: {
                    ...event.registration,
                    targetBatch: {
                      ...event.registration.targetBatch,
                      description: e.target.value
                    }
                  }
                })
              }
              placeholder="e.g., Batch 2024 - Silver Jubilee"
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}
    </div>
  </div>
                    </div>
                  </div>
                </div>
              </div>
  


            </div>
          </div>

          {/* Venue Section */}
          <div className={`${currentStep === 1 ? "block" : "hidden"}`}>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Venue Details
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Where will your event take place?
                    </p>
                  </div>
                  <Building className="h-5 w-5 text-gray-400" />
                </div>

                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-lg p-4 flex items-center">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={event.venue.isVirtual}
                        onChange={(e) =>
                          setEvent({
                            ...event,
                            venue: {
                              ...event.venue,
                              isVirtual: e.target.checked,
                            },
                          })
                        }
                        className="sr-only peer"
                      />
                      <div
                        className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 
                      peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full 
                      peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] 
                      after:left-[2px] after:bg-white after:border-gray-300 after:border 
                      after:rounded-full after:h-5 after:w-5 after:transition-all 
                      peer-checked:bg-blue-600"
                      ></div>
                    </label>
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      This is a virtual event
                    </span>
                  </div>

                  {event.venue.isVirtual ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Meeting Link
                      </label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                        <input
                          type="url"
                          value={event.venue.meetingLink}
                          onChange={(e) =>
                            setEvent({
                              ...event,
                              venue: {
                                ...event.venue,
                                meetingLink: e.target.value,
                              },
                            })
                          }
                          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 
                          focus:ring-2 focus:ring-blue-500"
                          placeholder="https://"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Venue Name
                        </label>
                        <input
                          type="text"
                          value={event.venue.name}
                          onChange={(e) =>
                            setEvent({
                              ...event,
                              venue: { ...event.venue, name: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 
                          focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Conference Hall, Auditorium"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Address
                        </label>
                        <textarea
                          value={event.venue.address}
                          onChange={(e) =>
                            setEvent({
                              ...event,
                              venue: {
                                ...event.venue,
                                address: e.target.value,
                              },
                            })
                          }
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 
                          focus:ring-2 focus:ring-blue-500"
                          rows={2}
                          placeholder="Complete venue address"
                          name="address"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City
                        </label>
                        <input
                          type="text"
                          value={event.venue.city}
                          onChange={(e) =>
                            setEvent({
                              ...event,
                              venue: { ...event.venue, city: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 
                          focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State
                        </label>
                        <input
                          type="text"
                          value={event.venue.state}
                          onChange={(e) =>
                            setEvent({
                              ...event,
                              venue: { ...event.venue, state: e.target.value },
                            })
                          }
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 
                          focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Pincode
                        </label>
                        <input
                          type="text"
                          value={event.venue.pincode}
                          onChange={(e) =>
                            setEvent({
                              ...event,
                              venue: {
                                ...event.venue,
                                pincode: e.target.value,
                              },
                            })
                          }
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 
                          focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Schedule Section */}
          {/* Replace your Schedule Section with this */}
          <div className={`${currentStep === 2 ? "block" : "hidden"}`}>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Event Schedule
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      When will your event take place?
                    </p>
                  </div>
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date & Time
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="datetime-local"
                        required
                        value={event.schedule.startDate}
                        onChange={(e) =>
                          setEvent({
                            ...event,
                            schedule: {
                              ...event.schedule,
                              startDate: e.target.value,
                            },
                          })
                        }
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date & Time
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="datetime-local"
                        required
                        value={event.schedule.endDate}
                        onChange={(e) =>
                          setEvent({
                            ...event,
                            schedule: {
                              ...event.schedule,
                              endDate: e.target.value,
                            },
                          })
                        }
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Registration Section */}
          <div className={`${currentStep === 3 ? "block" : "hidden"}`}>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Registration Details
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Set up your event registration
                    </p>
                  </div>
                  <Users className="h-5 w-5 text-gray-400" />
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maximum Capacity
                    </label>
                    <div className="relative">
                      <Users className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="number"
                        min="0"
                        value={event.capacity.max}
                        onChange={(e) =>
                          setEvent({
                            ...event,
                            capacity: { max: e.target.value },
                          })
                        }
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                        placeholder="Number of participants"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Registration Deadline
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="datetime-local"
                        value={event.registration.deadline}
                        onChange={(e) =>
                          setEvent({
                            ...event,
                            registration: {
                              ...event.registration,
                              deadline: e.target.value,
                            },
                          })
                        }
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Registration Fee
                    </label>
                    <div className="relative flex space-x-4">
                      <div className="flex-1 relative">
                        <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                        <input
                          type="number"
                          min="0"
                          value={event.registration.fee.amount}
                          onChange={(e) =>
                            setEvent({
                              ...event,
                              registration: {
                                ...event.registration,
                                fee: {
                                  ...event.registration.fee,
                                  amount: e.target.value,
                                },
                              },
                            })
                          }
                          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                          placeholder="Amount"
                        />
                      </div>
                      <select
                        value={event.registration.fee.currency}
                        onChange={(e) =>
                          setEvent({
                            ...event,
                            registration: {
                              ...event.registration,
                              fee: {
                                ...event.registration.fee,
                                currency: e.target.value,
                              },
                            },
                          })
                        }
                        className="w-24 px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="INR">INR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Agenda Section */}
          <div className={`${currentStep === 4 ? "block" : "hidden"}`}>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Event Agenda
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Plan your event schedule
                    </p>
                  </div>
                  <Target className="h-5 w-5 text-gray-400" />
                </div>

                <div className="space-y-6">
                  {/* New Agenda Item Form */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="grid grid-cols-1 gap-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Time
                          </label>
                          <input
                            type="datetime-local"
                            value={newAgendaItem.time}
                            onChange={(e) =>
                              setNewAgendaItem({
                                ...newAgendaItem,
                                time: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Title
                          </label>
                          <input
                            type="text"
                            value={newAgendaItem.title}
                            onChange={(e) =>
                              setNewAgendaItem({
                                ...newAgendaItem,
                                title: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                            placeholder="Session title"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={newAgendaItem.description}
                          onChange={(e) =>
                            setNewAgendaItem({
                              ...newAgendaItem,
                              description: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                          rows={2}
                          placeholder="Session description"
                          name="description"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Speakers
                        </label>
                        <div className="space-y-4">
                          {newAgendaItem.speakers.map((speaker, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-4"
                            >
                              <div className="flex-1">
                                <input
                                  type="text"
                                  value={speaker}
                                  onChange={(e) =>
                                    updateSpeaker(index, e.target.value)
                                  }
                                  className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                                  placeholder="Speaker name"
                                />
                              </div>
                              {index > 0 && (
                                <button
                                  type="button"
                                  onClick={() => removeSpeaker(index)}
                                  className="text-gray-400 hover:text-red-500"
                                >
                                  <X className="h-5 w-5" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={addSpeaker}
                            className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Another Speaker
                          </button>
                        </div>
                      </div>

                      <div>
                        <button
                          type="button"
                          onClick={addAgendaItem}
                          className="w-full flex items-center justify-center px-4 py-2.5 border border-transparent 
                  rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Plus className="h-5 w-5 mr-2" />
                          Add to Agenda
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Agenda Items List */}
                  <div className="space-y-4">
                    {event.agenda.map((item, index) => (
                      <div
                        key={index}
                        className="bg-white border rounded-lg p-4 flex justify-between items-start"
                      >
                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="h-4 w-4 mr-1.5" />
                            {new Date(item.time).toLocaleString()}
                          </div>
                          <h4 className="font-medium text-gray-900">
                            {item.title}
                          </h4>
                          {item.description && (
                            <p className="text-sm text-gray-600">
                              {item.description}
                            </p>
                          )}
                          {item.speakers?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {item.speakers.map((speaker, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center text-sm text-blue-600"
                                >
                                  <Users className="h-4 w-4 mr-1.5" />
                                  {speaker}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAgendaItem(index)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Navigation Buttons */}
          <div className="flex items-center justify-between pt-6">
            <button
              type="button" // Explicitly set type
              onClick={(e) => {
                e.preventDefault(); // Prevent any form submission
                if (currentStep > 0) {
                  setCurrentStep(currentStep - 1);
                }
              }}
              className={`px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 
      flex items-center ${
        currentStep === 0 ? "opacity-50 cursor-not-allowed" : ""
      }`}
              disabled={currentStep === 0}
            >
              Previous
            </button>

            <div className="flex items-center gap-4">
              <button
                type="button" // Explicitly set type
                onClick={(e) => {
                  e.preventDefault(); // Prevent any form submission
                  navigate("/college/events");
                }}
                className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>

              {currentStep < formSteps.length - 1 ? (
                <button
                  type="button" // Explicitly set type
                  onClick={(e) => {
                    e.preventDefault(); // Prevent any form submission
                    setCurrentStep(currentStep + 1);
                  }}
                  className="px-6 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 
          flex items-center"
                >
                  Next Step
                  <ChevronRight className="ml-2 h-4 w-4" />
                </button>
              ) : (
                <button
                  type="submit" // This is the only submit button
                  disabled={loading}
                  className="px-6 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 
          flex items-center disabled:bg-blue-400"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-5 w-5" />
                      Creating Event...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-5 w-5" />
                      Create Event
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEventCollege;